import {
  HiCheckCircle,
  HiPencilSquare,
  HiTrash,
  HiExclamationTriangle,
  HiXMark,
} from 'react-icons/hi2';

/**
 * Toast — drop-in restyle of the legacy alert box. Same props contract
 * ({alertStatus, onCloseAlert}), so the ~12 consuming views are untouched.
 * Colors come from the Netlise design tokens (see src/index.css), so the
 * toast follows light/dark theme automatically.
 */
const VARIANTS: Record<
  string,
  {title: string; sub: string; color: string; bg: string; Icon: any}
> = {
  CREATE: {
    title: 'Successfully Created',
    sub: 'Your changes have been saved.',
    color: 'var(--pos)',
    bg: 'var(--posSoft)',
    Icon: HiCheckCircle,
  },
  UPDATE: {
    title: 'Successfully Updated',
    sub: 'Your changes have been saved.',
    color: 'var(--info)',
    bg: 'var(--infoSoft)',
    Icon: HiPencilSquare,
  },
  DELETE: {
    title: 'Successfully Deleted',
    sub: 'The item has been removed.',
    color: 'var(--neg)',
    bg: 'var(--negSoft)',
    Icon: HiTrash,
  },
  FAILED: {
    title: 'Something went wrong',
    sub: 'The action could not be completed.',
    color: 'var(--neg)',
    bg: 'var(--negSoft)',
    Icon: HiExclamationTriangle,
  },
};

const Alert = (props: any) => {
  const {alertStatus, onCloseAlert} = props;
  const {isOpenSuccessAlert, isOpenFailedAlert, alertType} = alertStatus;

  if (!isOpenSuccessAlert && !isOpenFailedAlert) return null;

  const variant = isOpenFailedAlert
    ? VARIANTS.FAILED
    : VARIANTS[alertType] ?? VARIANTS.DELETE;
  const {title, sub, color, bg, Icon} = variant;

  // Legacy branches passed 'CREATE' | 'UPDATE' | 'DELETE' back on close.
  const closeStatus =
    alertType === 'CREATE' || alertType === 'UPDATE' ? alertType : 'DELETE';

  return (
    <div className="fixed top-16 right-5 z-[1200] nl-rise">
      <div
        className="flex items-center gap-3 bg-surface border border-borderc rounded-card shadow-pop pl-3 pr-2 py-2.5 min-w-[320px] max-w-[420px]"
        role="status"
      >
        <span
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{background: bg}}
        >
          <Icon size={20} style={{color}} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text1 leading-tight">
            {title}
          </p>
          <p className="text-xs text-text3 mt-0.5">{sub}</p>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => onCloseAlert(closeStatus)}
          className="w-8 h-8 rounded-lg text-text3 hover:bg-surface2 hover:text-text1 flex items-center justify-center transition-colors flex-shrink-0"
        >
          <HiXMark size={16} />
        </button>
      </div>
    </div>
  );
};
export default Alert;
